<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { IMAGE_ACCEPT } from "@/modules/constant";
import { MediaUploadFileItem } from "@/types/component";
import { useMedia } from "@/stores/media";
import { GenderEnum } from "@/enums/mateEnums";
import { showLoadingToast } from "vant";
import { useMate } from "@/stores/mate";
import CropImageOverlay, {
  CropImageOverlayInstance,
} from "@/components/mobile/media/CropImageOverlay.vue";

const props = defineProps<{
  gender: GenderEnum;
  referenceMedia: Pick<Media, "id" | "url"> | null;
  generatedMedia: Media | null;
}>();
const emit = defineEmits<{
  (e: "update:referenceMedia", value: Pick<Media, "id" | "url"> | null): void;
  (e: "update:generatedMedia", value: Media | null): void;
}>();
const { t } = useI18n();
const media = useMedia();
const mate = useMate();

const genderImgUrl = computed(() => {
  if (props.gender === GenderEnum.FEMALE) {
    return "/img/logo/logo-grayscale.webp";
  } else {
    return "/img/logo/logo-grayscale.webp";
  }
});
const cropper = ref<CropImageOverlayInstance | null>(null);
const uploadFiles = ref<MediaUploadFileItem[]>([]);

const onImageUpload = async (file: MediaUploadFileItem) => {
  cropper.value?.showCropper(file.content!);
  uploadFiles.value = [];
};
const onCropConfirm = async (_base64: string, file: File) => {
  // 1. 获取裁剪后的图片
  const loading = showLoadingToast(t("Message.uploading"));
  try {
    const uploadItem: MediaUploadFileItem = { file, mediaId: 0 };
    const res = await media.uploadMediaItem(
      uploadItem,
      mate.currentMate!.id!,
      6
    );
    emit("update:referenceMedia", { id: res.mediaId, url: res.url! });
  } finally {
    loading.close();
  }
};
</script>
<template>
  <div class="flex flex-col gap-10 items-center justify-center">
    <div class="text-white text-[2rem] font-bold text-center">
      <template v-if="referenceMedia">
        {{ $t("loginmodal.characterAppearance") }}
      </template>
      <template v-else>
        {{ $t("loginmodal.setAvatar") }}
      </template>
    </div>
    <div v-if="!referenceMedia" class="relative">
      <!-- 头像 -->
      <div
        class="rounded overflow-hidden shadow-lg pointer-events-none blur-sm w-52 aspect-[3/4]"
      >
        <base-img
          :url="genderImgUrl"
          :width="208"
          fit="object-contain"
          ratio="3:4"
        />
      </div>
      <!-- 选择按钮 -->
      <div
        class="absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center gap-4"
      >
        <van-uploader
          v-model="uploadFiles"
          :accept="IMAGE_ACCEPT"
          :after-read="onImageUpload"
          :max-count="2"
          :preview-image="false"
          reupload
        >
          <button class="btn btn-neutral rounded">
            <i class="iconify mingcute--pic-line" />
            {{ $t("loginmodal.selectPhoto") }}
          </button>
        </van-uploader>
      </div>
    </div>
    <div v-else class="relative flex flex-col gap-2">
      <!-- 头像 -->
      <div
        class="rounded overflow-hidden shadow-lg pointer-events-none w-52 aspect-[3/4]"
      >
        <VanImage
          :src="referenceMedia.url"
          class="w-full aspect-[3/4]"
          fit="object-contain"
          crossorigin="anonymous"
        />
      </div>
      <!-- 选择按钮 -->
      <div class="flex flex-col items-center justify-center gap-4">
        <van-uploader
          v-model="uploadFiles"
          :accept="IMAGE_ACCEPT"
          :after-read="onImageUpload"
          :max-count="2"
          :preview-image="false"
          reupload
        >
          <button class="btn btn-neutral rounded">
            <i class="iconfont icon-refresh" />
            {{ $t("trait.reselect") }}
          </button>
        </van-uploader>
      </div>
    </div>
    <crop-image-overlay
      ref="cropper"
      :crop-aspect-ratio="3 / 4"
      :on-finished="onCropConfirm"
    />
  </div>
</template>
